/**
 * trainingExam — the exam grading seam, built to the TR-5 server contract
 * (binding fix B) so the move to the server is a swap of internals, not a
 * rewrite of the screens:
 *
 *   - `startExam(test)` pins the question set (shuffled, `TESTS[test].qCount`),
 *     the `passMark`, and the `contentVersion` at START. Nothing at finish time
 *     re-selects the set or the manifest.
 *   - `gradeExam(spec, answers)` accepts a submission ONLY if it is EXACTLY the
 *     pinned set (same length, no duplicates, set-equal — else AnswersMismatch),
 *     then grades against the pinned answer key. The score is computed here from
 *     the manifest, never taken from a client-reported number.
 *
 * For now this runs client-side; TR-5 replaces `startExam`/`gradeExam` with
 * `POST /training/exams` and `/finish`, where the server owns both. Historical
 * grading against a prior `contentVersion` manifest (binding fix D) also lands
 * with TR-5's per-version manifest retention — today there is one version.
 */
import {
  questions,
  tests,
  CONTENT_VERSION,
  type TestKey,
  type TrainingQuestion,
} from '@/content/training';

export interface ExamSpec {
  test: TestKey;
  /** the pinned question ids, in presentation order */
  questionIds: string[];
  passMark: number;
  total: number;
  contentVersion: string;
  startedAt: number;
}

export interface ExamAnswer {
  questionId: string;
  /** chosen choice index, or null for a skipped question */
  choice: number | null;
}

export interface GradedAnswer {
  question: TrainingQuestion;
  choice: number | null;
  correct: boolean;
}

export interface ExamResult {
  test: TestKey;
  score: number;
  total: number;
  pct: number;
  passed: boolean;
  contentVersion: string;
  graded: GradedAnswer[];
}

export class AnswersMismatchError extends Error {
  constructor(reason: string) {
    super(`answers_mismatch: ${reason}`);
    this.name = 'AnswersMismatchError';
  }
}

function shuffle<T>(a: T[]): T[] {
  const out = a.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Pin an exam at START: the set, the pass mark, and the content version. */
export function startExam(test: TestKey): ExamSpec {
  const t = tests[test];
  const picked = shuffle(questions.filter((q) => q.test === test)).slice(0, t.qCount);
  return {
    test,
    questionIds: picked.map((q) => q.id),
    passMark: t.pass,
    total: picked.length,
    contentVersion: CONTENT_VERSION,
    startedAt: Date.now(),
  };
}

/**
 * Grade a finished exam against its pinned set + answer key. Throws
 * AnswersMismatchError unless the submission is exactly the pinned set.
 */
export function gradeExam(spec: ExamSpec, answers: ExamAnswer[]): ExamResult {
  const submitted = answers.map((a) => a.questionId);
  if (submitted.length !== spec.questionIds.length) {
    throw new AnswersMismatchError('wrong length');
  }
  const submittedSet = new Set(submitted);
  if (submittedSet.size !== submitted.length) {
    throw new AnswersMismatchError('duplicate question');
  }
  const pinned = new Set(spec.questionIds);
  for (const id of submitted) {
    if (!pinned.has(id)) throw new AnswersMismatchError('unknown question');
  }
  // length + no-duplicates + every-submitted-is-pinned ⇒ set-equal.

  const choiceByQ = new Map(answers.map((a) => [a.questionId, a.choice]));
  const byId = new Map(questions.map((q) => [q.id, q]));
  const graded: GradedAnswer[] = spec.questionIds.map((id) => {
    const question = byId.get(id)!; // pinned-version manifest lookup
    const choice = choiceByQ.get(id) ?? null;
    return { question, choice, correct: choice === question.answer };
  });
  const score = graded.filter((g) => g.correct).length;
  const pct = Math.round((score * 100) / spec.total);
  return {
    test: spec.test,
    score,
    total: spec.total,
    pct,
    passed: score >= spec.passMark,
    contentVersion: spec.contentVersion,
    graded,
  };
}
